import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import HeroSection from './components/HeroSection';
import IntroSection from './components/IntroSection';
import ServicesSection from './components/ServicesSection';
import WhySection from './components/WhySection';
import CtaSection from './components/CtaSection';
import { useSEO } from '@/hooks/useSEO';

const schemaData = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebSite',
      '@id': `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/#website`,
      url: import.meta.env.VITE_SITE_URL || 'https://example.com',
      name: 'Advantixx',
      description: 'ACHC Home Health Accreditation Consultants — expert consulting for home health agencies.',
      potentialAction: {
        '@type': 'SearchAction',
        target: {
          '@type': 'EntryPoint',
          urlTemplate: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/blog?q={search_term_string}`,
        },
        'query-input': 'required name=search_term_string',
      },
    },
    {
      '@type': 'ProfessionalService',
      '@id': `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/#organization`,
      name: 'Advantixx Home Health Accreditation Consultants',
      url: import.meta.env.VITE_SITE_URL || 'https://example.com',
      logo: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/logo.png`,
      description: 'Expert ACHC accreditation consulting for home health agencies. Mock surveys, policy development, compliance audits, staff training, and ongoing support.',
      founder: {
        '@type': 'Person',
        name: 'Dr. Osmel Villarreal',
        jobTitle: 'ACHC Certified Consultant, DBA, MBA, MS',
      },
      serviceType: 'ACHC Accreditation Consulting',
      areaServed: {
        '@type': 'Country',
        name: 'United States',
      },
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'ACHC Accreditation Services',
        itemListElement: [
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'ACHC Accreditation Preparation' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Mock Surveys & Readiness Assessments' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Policy & Procedure Development' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Compliance Audits' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Staff Training' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Ongoing Compliance Support' } },
        ],
      },
      sameAs: ['https://www.achc.org/certified-consultants.html'],
    },
  ],
};

export default function HomePage() {
  useSEO({
    title: 'Advantixx | ACHC Home Health Accreditation Consultants',
    description: 'Advantixx Home Health Accreditation Consultants — expert ACHC accreditation consulting for home health agencies. Mock surveys, policy development, compliance audits, staff training, and ongoing support.',
    keywords: 'ACHC accreditation, home health consulting, ACHC certified consultant, compliance audit, home health agency',
    canonical: '/',
    ogType: 'website',
  });

  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-home';
    script.text = JSON.stringify(schemaData);
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById('schema-home');
      if (el) el.remove();
    };
  }, []);

  return (
    <div className="bg-[#0d0d0d] min-h-screen">
      <Navbar />
      <main>
        <HeroSection />
        <IntroSection />
        <ServicesSection />
        <WhySection />
        <CtaSection />
      </main>
      <Footer />
    </div>
  );
}
